// The ONE announcement UI. The tenant screen, the platform console tab and the
// partner console tab are thin wrappers that pass a different `scope` — the list,
// the form and the actions are not duplicated per surface (plan PR-2 §0).
//
// Layout follows the existing admin screens: an inline form panel (like
// `CreditLimitForm`) rather than a modal, and `window.confirm` for the two
// irreversible-ish transitions — admin-web has no modal primitive, and inventing
// one here would mean hand-rolling focus management with no DOM test to hold it.

import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  Announcement,
  AnnouncementScope,
  AnnouncementSeverity,
  AnnouncementStatus,
} from '../../api.js';
import {
  ANNOUNCEMENT_BODY_MAX,
  ANNOUNCEMENT_SEVERITIES,
  ANNOUNCEMENT_STATUSES,
  ANNOUNCEMENT_TITLE_MAX,
} from '../../../../shared-types/src/index.js';
import { DataTable } from '../../components/DataTable.js';
import { describeError } from '../../errors.js';
import { useFetchOnMount } from '../../hooks/useFetchOnMount.js';
import type { AdminHttpClient } from '../../http.js';
import type { ToastMessage } from '../../types.js';
import {
  announcementOffset,
  clampAnnouncementPage,
  isAnnouncementExpiringSoon,
  visibleAnnouncementActions,
} from './announcement-rules.js';

const PAGE_SIZE = 20;

// Derived from the shared unions rather than re-typed: a fourth severity would
// otherwise appear in the API and silently never reach this dropdown.
const SEVERITIES: readonly AnnouncementSeverity[] = ANNOUNCEMENT_SEVERITIES;
const STATUS_FILTERS: Array<AnnouncementStatus | ''> = ['', ...ANNOUNCEMENT_STATUSES];

interface FormState {
  /** Set while editing an existing row. */
  id?: string;
  title: string;
  body: string;
  severity: AnnouncementSeverity;
  /** `datetime-local` value (local wall clock, no zone) or ''. */
  expiresAt: string;
  /** The stored ISO the field was filled from. `datetime-local` has minute
   *  precision, so re-saving an untouched field would otherwise round a
   *  server-set `…:00:45Z` down to `…:00:00Z`. */
  expiresAtOriginal?: string | null;
}

const EMPTY_FORM: FormState = {
  title: '',
  body: '',
  severity: 'info',
  expiresAt: '',
};

/** The expiry to send: the untouched original when the field still shows it,
 *  otherwise whatever the operator picked. */
export function resolveExpiry(form: {
  expiresAt: string;
  expiresAtOriginal?: string | null;
}): string | null {
  const picked = localInputToIso(form.expiresAt);
  const original = form.expiresAtOriginal ?? null;
  if (original && picked && isoToLocalInput(original) === form.expiresAt)
    return original;
  return picked;
}

/** `datetime-local` gives local wall-clock text; the API takes ISO UTC. */
export function localInputToIso(value: string): string | null {
  if (!value.trim()) return null;
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed.toISOString() : null;
}

/** ISO → the `datetime-local` value that shows the same instant locally. */
export function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function AnnouncementsManager({
  http,
  scope,
  canWrite,
  onToast,
  active = true,
  refetchKey,
}: {
  http: AdminHttpClient;
  scope: AnnouncementScope;
  /** False for read-only roles: the write controls are not rendered at all. */
  canWrite: boolean;
  onToast: (toast: ToastMessage) => void;
  /** False while the surrounding console tab is hidden — skips the fetch. */
  active?: boolean;
  /** Refetch whenever this changes. The tenant surface passes the act-as tenant
   *  id: the client object and the screen instance both survive a tenant switch
   *  (the token is read per call), so without this the table would keep showing
   *  the previous tenant's rows under the new tenant's header. */
  refetchKey?: string;
}) {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState<Announcement[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState<AnnouncementStatus | ''>('');
  const [reloadNonce, setReloadNonce] = useState(0);
  const [form, setForm] = useState<FormState | undefined>();
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => setReloadNonce((n) => n + 1), []);

  // A tenant switch is a different data set, not a new page of the same one:
  // reset the pager and drop any open form, which would otherwise submit the
  // previous tenant's row id under the new tenant's token (a 404).
  useEffect(() => {
    setPage(0);
    setForm(undefined);
  }, [refetchKey]);

  // The nonce is part of the fetcher identity so a mutation re-runs it; the hook
  // supplies the monotonic-sequence guard that keeps a slow earlier response
  // (previous tenant, previous filter) from overwriting the current one.
  const fetcher = useCallback(() => {
    // `reloadNonce` and `refetchKey` are not read here — they are in the dep
    // array so a mutation or a tenant switch rebuilds this closure, which is
    // what re-runs the effect.
    void reloadNonce;
    void refetchKey;
    return http.listAnnouncements(scope, {
      limit: PAGE_SIZE,
      offset: announcementOffset(page, PAGE_SIZE),
      ...(status ? { status } : {}),
    });
  }, [http, scope, page, status, reloadNonce, refetchKey]);

  const onData = useCallback(
    (data: { items: Announcement[]; total: number }) => {
      setRows(data.items);
      setTotal(data.total);
      // Archiving the last row of the last page would otherwise strand the
      // viewer on a page that no longer exists.
      const clamped = clampAnnouncementPage(page, data.total, PAGE_SIZE);
      if (clamped !== page) setPage(clamped);
    },
    [page],
  );

  const onError = useCallback(
    (error: unknown) => onToast({ tone: 'error', message: describeError(error, t) }),
    [onToast, t],
  );

  useFetchOnMount({ enabled: active, fetcher, onData, onError });

  const locale = i18n.language;
  const formatTime = useCallback(
    (iso: string | null) => (iso ? new Date(iso).toLocaleString(locale) : '—'),
    [locale],
  );

  /** Every mutation funnels through here: one busy flag, one error mapping, and
   *  a refetch on success — a 404/409 means someone else moved the row, so the
   *  list must be reloaded either way. */
  const run = useCallback(
    async (
      action: () => Promise<unknown>,
      successKey: string,
      /** Only a successful FORM submit closes the form. A row action must not:
       *  publishing an older draft while a long body is half-typed would throw
       *  that draft away. */
      closesForm = false,
    ) => {
      setBusy(true);
      try {
        await action();
        onToast({ tone: 'success', message: t(successKey) });
        if (closesForm) setForm(undefined);
        reload();
      } catch (error) {
        onToast({ tone: 'error', message: describeError(error, t) });
        reload();
      } finally {
        setBusy(false);
      }
    },
    [onToast, reload, t],
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!form) return;
    const title = form.title.trim();
    const body = form.body.trim();
    if (!title || title.length > ANNOUNCEMENT_TITLE_MAX) {
      onToast({ tone: 'error', message: t('announcements.invalidTitle', { max: ANNOUNCEMENT_TITLE_MAX }) });
      return;
    }
    if (!body || body.length > ANNOUNCEMENT_BODY_MAX) {
      onToast({ tone: 'error', message: t('announcements.invalidBody', { max: ANNOUNCEMENT_BODY_MAX }) });
      return;
    }
    const expiresAt = resolveExpiry(form);
    if (form.expiresAt.trim() && expiresAt === null) {
      onToast({ tone: 'error', message: t('announcements.invalidExpiresAt') });
      return;
    }
    // The server re-checks this; the client check exists so a typo does not cost
    // a round trip. An unchanged past expiry on an existing row would also be
    // rejected server-side, which is the honest answer — it must be corrected
    // before the row can be saved again.
    if (expiresAt && Date.parse(expiresAt) <= Date.now()) {
      onToast({ tone: 'error', message: t('announcements.expiresAtPast') });
      return;
    }
    const id = form.id;
    if (id) {
      void run(
        () =>
          http.patchAnnouncement(scope, {
            id,
            title,
            body,
            severity: form.severity,
            expiresAt,
          }),
        'announcements.toast.updated',
        true,
      );
      return;
    }
    void run(
      () => http.createAnnouncement(scope, { title, body, severity: form.severity, expiresAt }),
      'announcements.toast.created',
      true,
    );
  };

  const columns = useMemo(
    () => [
      {
        key: 'title',
        header: t('announcements.col.title'),
        // A text node, never raw-HTML injection: the body is plain text, and a
        // pasted `<img onerror=…>` must read as characters.
        render: (row: Announcement) => <span title={row.title}>{row.title}</span>,
      },
      {
        key: 'severity',
        header: t('announcements.col.severity'),
        render: (row: Announcement) => (
          <span className={`badge severity-${row.severity}`}>
            {t(`announcements.severity.${row.severity}`)}
          </span>
        ),
      },
      {
        key: 'status',
        header: t('announcements.col.status'),
        render: (row: Announcement) => (
          <span className={`badge status-${row.status}`}>
            {t(`announcements.status.${row.status}`)}
          </span>
        ),
      },
      {
        key: 'publishedAt',
        header: t('announcements.col.publishedAt'),
        render: (row: Announcement) => formatTime(row.publishedAt),
      },
      {
        key: 'expiresAt',
        header: t('announcements.col.expiresAt'),
        render: (row: Announcement) =>
          isAnnouncementExpiringSoon(row.expiresAt, Date.now(), {
            status: row.status,
          }) ? (
            <span className="warning-text" title={t('announcements.expiringSoon')}>
              {formatTime(row.expiresAt)}
            </span>
          ) : (
            formatTime(row.expiresAt)
          ),
      },
      {
        key: 'updatedAt',
        header: t('announcements.col.updatedAt'),
        render: (row: Announcement) => formatTime(row.updatedAt),
      },
      {
        key: 'actions',
        header: t('announcements.col.actions'),
        render: (row: Announcement) => {
          const actions = visibleAnnouncementActions(row.status, canWrite);
          if (actions.length === 0) return <span className="muted">—</span>;
          return (
            <div className="row-actions">
              {actions.includes('edit') ? (
                <button
                  type="button"
                  className="ghost"
                  disabled={busy}
                  onClick={() =>
                    setForm({
                      id: row.id,
                      title: row.title,
                      body: row.body,
                      severity: row.severity,
                      expiresAt: isoToLocalInput(row.expiresAt),
                      expiresAtOriginal: row.expiresAt,
                    })
                  }
                >
                  {t('announcements.action.edit')}
                </button>
              ) : null}
              {actions.includes('publish') ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    if (!window.confirm(t('announcements.confirm.publish', { title: row.title }))) return;
                    void run(
                      () => http.publishAnnouncement(scope, row.id),
                      'announcements.toast.published',
                    );
                  }}
                >
                  {t('announcements.action.publish')}
                </button>
              ) : null}
              {actions.includes('archive') ? (
                <button
                  type="button"
                  className="ghost"
                  disabled={busy}
                  onClick={() => {
                    if (!window.confirm(t('announcements.confirm.archive', { title: row.title }))) return;
                    void run(
                      () => http.archiveAnnouncement(scope, row.id),
                      'announcements.toast.archived',
                    );
                  }}
                >
                  {t('announcements.action.archive')}
                </button>
              ) : null}
            </div>
          );
        },
      },
    ],
    [t, canWrite, busy, formatTime, http, scope, run],
  );

  return (
    <section className="content-stack">
      <div className="toolbar-form">
        {/* A select, not a second tab strip: tabs group sibling SCREENS in this
            console, so a second row of tabs would overload the same grammar. */}
        <label>
          <span>{t('announcements.filter.label')}</span>
          <select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as AnnouncementStatus | '');
              setPage(0);
            }}
          >
            {STATUS_FILTERS.map((value) => (
              <option key={value || 'all'} value={value}>
                {value ? t(`announcements.status.${value}`) : t('announcements.filter.all')}
              </option>
            ))}
          </select>
        </label>
        {canWrite ? (
          <button type="button" onClick={() => setForm({ ...EMPTY_FORM })} disabled={busy}>
            {t('announcements.new')}
          </button>
        ) : null}
      </div>

      {form ? (
        <form className="panel announcement-form" onSubmit={submit}>
          <h2>{form.id ? t('announcements.editTitle') : t('announcements.newTitle')}</h2>
          <label>
            <span>{t('announcements.form.title')}</span>
            <input
              value={form.title}
              maxLength={ANNOUNCEMENT_TITLE_MAX}
              onChange={(event) => setForm({ ...form, title: event.target.value })}
              disabled={busy}
              autoComplete="off"
            />
          </label>
          <label>
            <span>{t('announcements.form.body')}</span>
            {/* Plain text only — no markdown toolbar and no preview, because
                nothing renders the body as markup. */}
            <textarea
              value={form.body}
              rows={6}
              maxLength={ANNOUNCEMENT_BODY_MAX}
              onChange={(event) => setForm({ ...form, body: event.target.value })}
              disabled={busy}
            />
          </label>
          <label>
            <span>{t('announcements.form.severity')}</span>
            <select
              value={form.severity}
              onChange={(event) =>
                setForm({ ...form, severity: event.target.value as AnnouncementSeverity })
              }
              disabled={busy}
            >
              {SEVERITIES.map((severity) => (
                <option key={severity} value={severity}>
                  {t(`announcements.severity.${severity}`)}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>{t('announcements.form.expiresAt')}</span>
            <input
              type="datetime-local"
              value={form.expiresAt}
              onChange={(event) => setForm({ ...form, expiresAt: event.target.value })}
              disabled={busy}
            />
          </label>
          <p className="muted">{t('announcements.form.expiresAtHint')}</p>
          <div className="row-actions">
            <button disabled={busy}>{t('announcements.form.save')}</button>
            <button type="button" className="ghost" onClick={() => setForm(undefined)} disabled={busy}>
              {t('announcements.form.cancel')}
            </button>
          </div>
        </form>
      ) : null}

      <DataTable
        rows={rows}
        empty={t('announcements.empty')}
        pageSize={PAGE_SIZE}
        getRowKey={(row) => row.id}
        server={{ total, page, onPageChange: setPage }}
        columns={columns}
      />
    </section>
  );
}
